import { Alert, Button, Card, Center, Loader, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { checkPasswordLink, setPassword as setPasswordCall } from '../api/gen';

/** Where the "new password" mail lands; the server puts this path in the link. */
export const SET_PASSWORD_PATH = '/auth/set-password';

/**
 * The link is checked before anything is typed, and checking does not spend
 * it: somebody who opens a dead link should be told so on arrival rather than
 * after choosing a password they then have to choose again.
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [link, setLink] = useState<'checking' | 'good' | 'dead'>('checking');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setLink('dead');
      setError('This link has no token.');
      return;
    }
    void (async () => {
      try {
        await unwrap(checkPasswordLink({ body: { token } }));
        setLink('good');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setLink('dead');
      }
    })();
  }, [token]);

  const submit = () => {
    setBusy(true);
    setError('');
    void (async () => {
      try {
        await unwrap(setPasswordCall({ body: { token, password } }));
        setDone(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Center mih="100vh" p="md">
      <Card withBorder p="xl" w={400} maw="100%">
        <Stack>
          <Title order={3}>Choose a new password</Title>
          {link === 'checking' && <Loader size="sm" />}
          {error && (
            <Alert color="red" variant="light" role="alert">
              {error}
            </Alert>
          )}
          {done ? (
            <>
              <Text size="sm">The password is set, and every earlier session has been ended.</Text>
              <Button component="a" href="/">
                Sign in
              </Button>
            </>
          ) : link === 'good' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (password) submit();
              }}
            >
              <Stack>
                <PasswordInput
                  label="New password"
                  description="At least twelve characters."
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                />
                <Button type="submit" disabled={!password} loading={busy}>
                  Set password
                </Button>
              </Stack>
            </form>
          ) : link === 'dead' ? (
            <Button component="a" href="/" variant="light">
              Back to sign-in
            </Button>
          ) : null}
        </Stack>
      </Card>
    </Center>
  );
}
